import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import UserLayout from "@/components/layouts/UserLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Wallet,
  Clock,
  CheckCircle,
  ArrowUpRight,
  Banknote,
  AlertCircle,
  Building2,
} from "lucide-react";
import { toast } from "sonner";

const UserWallet = () => {
  const { user, getAuthHeaders } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    account_holder_name: "",
  });

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await axios.get(`${API}/users/wallet`, {
        headers: getAuthHeaders(),
      });
      setWalletData(res.data);
    } catch (error) {
      toast.error("Failed to load wallet");
    }
    setLoading(false);
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    
    if (user?.kyc_status !== "approved") {
      toast.error("Please complete KYC verification first");
      return;
    }

    setWithdrawing(true);
    try {
      await axios.post(`${API}/users/withdraw`, withdrawForm, {
        headers: getAuthHeaders(),
      });
      toast.success("Withdrawal request submitted!");
      setWithdrawOpen(false);
      setWithdrawForm({
        amount: "",
        bank_name: "",
        account_number: "",
        ifsc_code: "",
        account_holder_name: "",
      });
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit withdrawal");
    }
    setWithdrawing(false);
  };

  const wallet = walletData?.wallet || { pending_balance: 0, approved_balance: 0, withdrawn_balance: 0 };
  const transactions = walletData?.transactions || [];
  const withdrawals = walletData?.withdrawals || [];

  if (loading) {
    return (
      <UserLayout title="Wallet">
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-2xl" />
            ))}
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout title="Wallet">
      {/* Wallet Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-yellow-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-500">Pending</span>
            </div>
            <p className="text-3xl font-bold text-yellow-600 font-heading">
              ₹{wallet.pending_balance.toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">Awaiting confirmation</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Approved</span>
            </div>
            <p className="text-3xl font-bold text-green-600 font-heading">
              ₹{wallet.approved_balance.toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">Available for withdrawal</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Banknote className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">Withdrawn</span>
            </div>
            <p className="text-3xl font-bold text-blue-600 font-heading">
              ₹{wallet.withdrawn_balance.toLocaleString()}
            </p>
            <p className="text-xs text-gray-400 mt-1">Successfully withdrawn</p>
          </CardContent>
        </Card>
      </div>

      {/* Withdraw Button */}
      <Card className="border-0 shadow-sm mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-semibold text-gray-900">
                Withdraw Funds
              </h3>
              <p className="text-sm text-gray-500">
                Minimum withdrawal: ₹100 • KYC required
              </p>
            </div>
            <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full px-8"
                  disabled={wallet.approved_balance < 100 || user?.kyc_status !== "approved"}
                  data-testid="withdraw-btn"
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Withdraw
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Withdrawal</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleWithdraw} className="space-y-4">
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input
                      type="number"
                      min="100"
                      max={wallet.approved_balance}
                      value={withdrawForm.amount}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                      placeholder="Enter amount"
                      required
                      data-testid="withdraw-amount-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Available: ₹{wallet.approved_balance}
                    </p>
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input
                      value={withdrawForm.bank_name}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, bank_name: e.target.value })}
                      placeholder="e.g., HDFC Bank"
                      required
                    />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input
                      value={withdrawForm.account_number}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, account_number: e.target.value })}
                      placeholder="Enter account number"
                      required
                    />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input
                      value={withdrawForm.ifsc_code}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, ifsc_code: e.target.value })}
                      placeholder="e.g., HDFC0001234"
                      required
                    />
                  </div>
                  <div>
                    <Label>Account Holder Name</Label>
                    <Input
                      value={withdrawForm.account_holder_name}
                      onChange={(e) => setWithdrawForm({ ...withdrawForm, account_holder_name: e.target.value })}
                      placeholder="Name as per bank account"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                    disabled={withdrawing}
                    data-testid="submit-withdraw-btn"
                  >
                    {withdrawing ? "Processing..." : "Submit Request"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {user?.kyc_status !== "approved" && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-xl flex items-center gap-2 text-yellow-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">
                Complete KYC verification to withdraw funds
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Withdrawal History */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary-600" />
            Withdrawal History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {withdrawals.length > 0 ? (
            <div className="space-y-3">
              {withdrawals.map((w) => (
                <div
                  key={w.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      ₹{w.amount.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {w.bank_details?.bank_name} • {new Date(w.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge
                    className={
                      w.status === "approved"
                        ? "bg-green-100 text-green-700"
                        : w.status === "rejected"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }
                  >
                    {w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No withdrawal history</p>
            </div>
          )}
        </CardContent>
      </Card>
    </UserLayout>
  );
};

export default UserWallet;
